import { useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';
import { errorsCode } from '../Login.enums';
import { useNavigate } from 'react-router-dom';
import { loggerInfo } from 'utils/infoLog';
import { useContextSelector } from 'use-context-selector';
import { AuthenticationContext } from 'contexts/Auth';
import { getMeInfo } from 'services/services';
import { useSessionStorage } from 'hooks/useSessionStorage';
import Analytics from 'helpers/analytics';
import { useToast } from '@welbe/web-design-system';

type UserProps = {
	email: string;
	type: 'confirmation' | '2fa';
};

const buttonText = {
	confirmation: 'Confirmar e-mail',
	'2fa': 'Enviar código',
};

export const useVerificationCode = () => {
	const { toast } = useToast();
	const { setSessionStorage } = useSessionStorage();
	const { cognitoUser, handleStorageAuth } = useContextSelector(
		AuthenticationContext,
		(contextProps: any) => contextProps,
	);
	const [isSuccess, setSuccess] = useState<boolean>(false);
	const [isLoginLoading, setLoginLoading] = useState<boolean>(false);
	const navigate = useNavigate();
	const [isLoading, setLoading] = useState<boolean>(false);
	const [code, setCode] = useState<string>('');
	const [user, setUser] = useState<UserProps>({
		email: '',
		type: 'confirmation',
	});

	const handleResendCode = async () => {
		await Auth.resendSignUp(user.email.trim());
	};

	const handleToast = (message?: string) => {
		toast.error(message || 'Rellena los campos marcados.');
	};

	const fetchInitialInfo = async () => {
		setLoginLoading(true);
		try {
			const { data } = await getMeInfo();
			setSessionStorage('me', JSON.stringify(data.content));
			await handleStorageAuth(cognitoUser);
			setTimeout(() => {
				navigate('/');
			}, 200);
		} catch (error) {
			const code = JSON.parse(JSON.stringify(error))?.code;
			loggerInfo({
				data: error,
				context: 'login',
				location: 'VerificationCode',
				payloadRequest: { ...user, code },
			});
			handleToast('Hubo un error al intentar autenticar');
		} finally {
			setLoginLoading(false);
		}
	};

	const handleSubmit = async () => {
		setLoading(true);

		try {
			if (user.type === 'confirmation') {
				await Auth.confirmSignUp(user.email, code);
				setSuccess(true);
			}
			if (user.type === '2fa') {
				await Auth.sendCustomChallengeAnswer(cognitoUser, code);
				fetchInitialInfo();
			}
			Analytics.track('login', 'Email Validated', {
				email: user.email,
				type: user.type,
			});
		} catch (error: any) {
			Analytics.track('login', 'Error Email Validated', {
				email: user.email,
				type: user.type,
			});
			const code = JSON.parse(JSON.stringify(error))?.code;
			handleToast(
				errorsCode[code]
					? errorsCode[code]
					: 'Hubo un error al intentar realizar esta acción.',
			);
			loggerInfo({
				data: error,
				context: 'login',
				location: 'VerificationCode',
				payloadRequest: { ...user, code },
			});
		} finally {
			setLoading(false);
		}
	};

	const handleBack = () => {
		sessionStorage.clear();
		navigate('/login');
	};

	useEffect(() => {
		if (code) {
			handleSubmit();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [code]);

	useEffect(() => {
		const token = window.location.search.split('token=')[1];
		const params = JSON.parse(window.atob(token)) || {};

		setUser(params);

		return () => setUser({ email: '', type: 'confirmation' });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cognitoUser]);

	return {
		isSuccess,
		isLoading,
		user,
		isLoginLoading,
		buttonText,
		handleBack,
		handleResendCode,
		setCode,
		handleSubmit,
	};
};
